import {
  Box,
  Container,
  Divider,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import NextImage from 'next/image'
import { MapstackUserCases } from 'ui-components/components/organisms/MapstackUseCases'

import { CTAButtons } from './CTAButtons'
import image1 from './section-4-1.png'
import image2 from './section-4-2.png'
import image3 from './section-4-3.png'

export function Section4() {
  return (
    <Container maxWidth='xl'>
      <Paper
        variant='elevation'
        elevation={8}
        sx={{
          p: 2,
          borderRadius: 4
        }}
      >
        <Stack
          direction='column'
          spacing={8}
          my={8}
          alignItems='center'
        >
          <Typography
            variant='next:h2'
            component='h2'
            textAlign='center'
            fontWeight={700}
            maxWidth='27ch'
          >
            Goodbye Manual Mapping, Hello Instant Insights
          </Typography>

          <Typography
            component='p'
            variant='next:h6'
            textAlign='center'
            color='text.secondary'
            maxWidth='96ch'
          >
            Eliminate tedious manual processes. Upload your address data and
            instantly explore interactive maps that reveal clusters, heatmaps,
            and hidden trends.
          </Typography>

          <Stack
            direction='row'
            gap={4}
            flexWrap='wrap'
            justifyContent='center'
          >
            <HowItWorkCard
              image={image1}
              title='Upload Your Address Data'
              alt='Upload Your Address Data'
              description={
                <>
                  Easily import your address data from spreadsheets or CRM
                  exports. Mapstack automatically organizes your data so you can
                  focus on discovering insights.
                </>
              }
            />

            <HowItWorkCard
              image={image2}
              title='Instantly Visualize Your Map'
              alt='Instantly Visualize Your Map'
              description={
                <>
                  Within seconds, watch your addresses come to life on an
                  interactive map—complete with dynamic visualizations like
                  heatmaps and clusters.
                </>
              }
            />

            <HowItWorkCard
              image={image3}
              title='Analyze & Explore'
              alt='Analyze & Explore'
              description={
                <>
                  Apply filters and switch between views—such as heatmaps and
                  clustering—to uncover trends and opportunities you never knew
                  existed.
                </>
              }
            />
          </Stack>

          <Stack
            spacing={8}
            alignItems='center'
          >
            <Typography
              component='h2'
              variant='next:h3'
              fontWeight={700}
              textAlign='center'
            >
              Powerful Mapping Visualizations
            </Typography>

            <Typography
              component='p'
              variant='next:h6'
              textAlign='center'
              color='text.secondary'
              maxWidth='88%'
            >
              Easily customize your view—from cluster maps to heatmaps and
              aggregates—to reveal deeper insights and drive smarter decisions.
            </Typography>

            <MapstackUserCases />
          </Stack>

          <CTAButtons />
        </Stack>
      </Paper>
    </Container>
  )
}

function HowItWorkCard(props) {
  const { title, description, image, alt } = props
  return (
    <Box
      width='100%'
      maxWidth={420}
      component={Paper}
      variant='elevation'
      elevation={8}
      borderRadius={3}
    >
      <Typography
        variant='next:h5'
        component='h3'
        textAlign='left'
        fontWeight={700}
        p={3}
      >
        {title}
      </Typography>
      <Divider />
      <Stack
        p={3}
        spacing={3}
      >
        <Box
          component={Paper}
          variant='outlined'
          sx={{
            position: 'relative',
            aspectRatio: '16/9',
            width: '100%',
            fontStyle: 'italic',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <NextImage
            src={image}
            alt={alt}
            fill
          />
        </Box>
        <Typography
          component='p'
          variant='next:subtitle1'
          textAlign='left'
        >
          {description}
        </Typography>
      </Stack>
    </Box>
  )
}
